import { PlusIcon } from '@heroicons/react/24/outline';
import { memo } from 'react';
import type { ObjectDefinitionSpec } from 'shared-types';
import { T } from '../../../translation/src';
import { useEditorState, useObjectEditor } from '../EditorContext';
import { FieldRow, PanelButton, PanelSection, Segmented } from '../panels/fields';

/**
 * The object's 3D model: how it follows a placed object's size along each
 * axis, or a way to add one. The edit tab's object settings, and a smart
 * object's "edit model" (Test mode) show it.
 */
const ModelSettings_: React.FC = () => {
  const { store, ui, setUi } = useObjectEditor();
  const { spec } = useEditorState();
  const setSpec = (update: (s: ObjectDefinitionSpec) => ObjectDefinitionSpec) => store.apply(update);
  return (
    <PanelSection title={<T _str="3D model" />}>
      {spec.model3d ? (
        <>
          {(['u', 'n', 'h'] as const).map((axis) => (
            <FieldRow key={axis} label={axis === 'u' ? <T _str="width" swc /> : axis === 'n' ? <T _str="depth" swc /> : <T _str="height" swc />}>
              <Segmented
                value={spec.model3d?.scaleMode[axis] ?? 'stretch'}
                options={[
                  { value: 'stretch', label: <T _str="stretch" swc /> },
                  { value: 'uniform', label: <T _str="keep shape" swc /> },
                  { value: 'fixed', label: <T _str="fixed" swc /> },
                ]}
                onChange={(mode) => setSpec((s) => (s.model3d ? { ...s, model3d: { ...s.model3d, scaleMode: { ...s.model3d.scaleMode, [axis]: mode } } } : s))}
              />
            </FieldRow>
          ))}
          <p className="text-xs text-gray-500 dark:text-gray-400">
            <T _str="how the model follows a placed object's size along each axis" swc />
          </p>
          {ui.view === '2d' && (
            <PanelButton onClick={() => setUi((u) => ({ ...u, view: 'split' }))}>
              <T _str="show the 3D view" swc />
            </PanelButton>
          )}
        </>
      ) : (
        <>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            <T _str="optional: a 3D model shown in projects' 3D views, stretching with the object" swc />
          </p>
          <PanelButton onClick={() => setUi((u) => ({ ...u, view: u.view === '2d' ? 'split' : u.view }))}>
            <PlusIcon className="h-3.5 w-3.5" />
            <T _str="add a 3D model" swc />
          </PanelButton>
        </>
      )}
    </PanelSection>
  );
};

export const ModelSettings = memo(ModelSettings_);
